import { createStyles, keyframes } from 'antd-style'

import { reduceMotion, skxFadeLift } from 'src/theme/motion'

const CHAT_COLUMN_WIDTH = 768
const HERO_COLUMN_WIDTH = 870
const SUGGESTION_CARD_MAX_WIDTH = 340
const CHAT_BODY_PADDING_BLOCK = 10
const CHAT_BODY_PADDING_INLINE = 12
const COMPOSER_SHADOW = '0 4px 18px rgba(11, 20, 32, 0.06)'
const PAGE_CHROME_HEIGHT = 140
const SHELL_TOPBAR_HEIGHT = 48
const SHELL_FOOTER_HEIGHT = 32
const HISTORY_POPOVER_WIDTH = 320
const HISTORY_POPOVER_HEIGHT = 420
const ATTACHMENT_THUMBNAIL_HEIGHT = 96
const ATTACHMENT_THUMBNAIL_MAX_WIDTH = 192
const ATTACHMENT_NAME_MAX_WIDTH = 180

const skxShimmerSweep = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`
const skxDotOne = keyframes`0%, 24% { opacity: 0; } 25%, 92% { opacity: 1; } 100% { opacity: 0; }`
const skxDotTwo = keyframes`0%, 49% { opacity: 0; } 50%, 92% { opacity: 1; } 100% { opacity: 0; }`
const skxDotThree = keyframes`0%, 74% { opacity: 0; } 75%, 92% { opacity: 1; } 100% { opacity: 0; }`
const skxTextBlink = keyframes`
  0% { background-position: -200% 100%; }
  25% { background-position: -100% 100%; }
  50% { background-position: 0 100%; }
  75% { background-position: 100% 100%; }
  100% { background-position: 200% 100%; }
`

export const useStyles = createStyles(({ token }) => ({
  page: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
    minHeight: `calc(100vh - ${PAGE_CHROME_HEIGHT}px)`,
    position: 'relative'
  },

  pageConversation: {
    height: `calc(100vh - var(--impersonation-offset, 0px) - ${
      SHELL_TOPBAR_HEIGHT +
      SHELL_FOOTER_HEIGHT +
      token.padding +
      token.paddingLG
    }px)`,
    minHeight: 0,
    overflow: 'hidden'
  },

  composerContent: {
    paddingBlock: `${token.paddingXXS}px !important`
  },

  historyPopoverPanel: {
    width: `min(${HISTORY_POPOVER_WIDTH}px, calc(100vw - ${
      token.paddingLG * 2
    }px))`,
    height: `min(${HISTORY_POPOVER_HEIGHT}px, calc(100vh - ${
      token.paddingLG * 2
    }px))`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },

  historySidebar: {
    position: 'absolute',
    insetBlock: 0,
    insetInlineStart: 0,
    zIndex: 5,
    width: 280,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadiusLG,
    overflow: 'hidden',
    boxShadow: token.boxShadowSecondary
  },

  historyHeader: {
    flex: '0 0 auto',
    padding: '12px 14px',
    borderBottom: `1px solid ${token.colorBorderSecondary}`
  },

  historyHeaderTitle: {
    fontSize: token.fontSize,
    fontWeight: 600,
    color: token.colorText
  },

  historyPanelWrap: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },

  chatMain: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    width: '100%',
    maxWidth: HERO_COLUMN_WIDTH,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column'
  },

  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: CHAT_COLUMN_WIDTH,
    padding: '2px 0 10px'
  },

  topBarButton: {
    color: token.colorTextSecondary,

    '&:hover, &:focus': {
      color: token.colorText
    }
  },

  topBarLeft: {
    flex: 1,
    minWidth: 0
  },

  conversationSwitchButton: {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 0,
    maxWidth: '100%',
    flex: '0 1 auto',
    overflow: 'hidden',
    padding: `${token.paddingXXS}px ${token.paddingXS}px`,
    color: token.colorText,
    background: 'transparent',
    border: 0,
    borderRadius: token.borderRadius,
    gap: token.paddingXXS,
    cursor: 'pointer',
    textAlign: 'left',
    transition: `background ${token.motionDurationFast} ${token.motionEaseOut}`,

    '&:hover, &:focus-visible': {
      background: token.controlItemBgHover,
      outline: 0
    }
  },

  conversationSwitchTitle: {
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: '100%',
    fontSize: token.fontSize + 1
  },

  headerSelectorIcon: {
    flex: '0 0 auto',
    color: token.colorTextTertiary
  },

  composer: {
    width: '100%',
    background: token.colorBgContainer,
    borderColor: token.colorBorderSecondary,
    boxShadow: COMPOSER_SHADOW,
    transition: `border-color ${token.motionDurationMid} ${token.motionEaseInOut}, box-shadow ${token.motionDurationMid} ${token.motionEaseInOut}`,

    '&:focus-within': {
      borderColor: token.colorPrimaryBorder,
      boxShadow: `0 4px 22px color-mix(in srgb, ${token.colorPrimary} 12%, transparent)`
    },

    '&&& .ant-sender-prefix': {
      alignSelf: 'center'
    }
  },

  chatCard: {
    flex: 1,
    width: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: token.boxShadowTertiary
  },

  conversation: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },

  conversationHeader: {
    padding: '10px 16px',
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
    background: token.colorBgContainer,
    flex: '0 0 auto'
  },

  bubbleListWrap: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
  },

  bubbleList: {
    flex: 1,
    minHeight: 0,
    background: token.colorBgContainer,

    '&& .ant-bubble-list-scroll-content': {
      paddingInline: token.padding,
      paddingBlock: CHAT_BODY_PADDING_BLOCK
    },

    '&& .ant-bubble:first-child': {
      paddingBlockStart: 0
    },

    '&& .ant-bubble:last-child': {
      paddingBlockEnd: 0
    }
  },

  bubbleListCompact: {
    '&& .ant-bubble-list-scroll-content': {
      paddingInline: token.paddingMD
    }
  },

  scrollDownButton: {
    position: 'absolute',
    insetInlineStart: '50%',
    bottom: token.paddingMD,
    transform: 'translateX(-50%)',
    zIndex: 2,
    background: token.colorBgElevated,
    boxShadow: token.boxShadowSecondary
  },

  userText: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  },

  userBubble: {
    '& .ant-bubble-footer': {
      marginBlockStart: token.marginXS,
      opacity: 0,
      pointerEvents: 'none',
      transition: `opacity ${token.motionDurationFast} ${token.motionEaseOut}`
    },

    '&:hover .ant-bubble-footer, &:focus-within .ant-bubble-footer': {
      opacity: 1,
      pointerEvents: 'auto'
    }
  },

  conversationComposer: {
    flex: '0 0 auto',
    background: token.colorBgContainer,
    padding: `${CHAT_BODY_PADDING_BLOCK}px ${CHAT_BODY_PADDING_INLINE}px`
  },

  composerArea: {
    flex: '0 0 auto',
    padding: '12px 16px 16px'
  },

  composerField: {
    padding: token.paddingXXS,
    borderColor: token.colorBorder,
    boxShadow: 'none',

    '&:focus-within': {
      borderColor: token.colorPrimary
    }
  },

  aiHeaderName: {
    fontSize: token.fontSize + 1,
    color: token.colorText
  },

  aiActionsDivider: {
    margin: 0,
    height: 16
  },

  actionButton: {
    color: token.colorTextSecondary,
    transition: `color ${token.motionDurationFast}, background-color ${token.motionDurationFast}`,

    '&:hover, &:focus-visible': {
      color: token.colorText,
      backgroundColor: token.colorFillTertiary
    }
  },

  actionButtonDone: {
    color: token.colorSuccess,

    '&:hover, &:focus-visible': {
      color: token.colorSuccess
    }
  },

  feedbackActive: {
    color: token.colorPrimary,

    '&:hover, &:focus-visible': {
      color: token.colorPrimaryHover
    }
  },

  enterSlow: {
    animation: `${skxFadeLift} ${token.motionDurationSlow} ${token.motionEaseOut} both`,
    [reduceMotion]: { animation: 'none' }
  },

  thinkingShimmer: {
    display: 'inline-block',
    fontSize: token.fontSizeSM,
    fontWeight: 500,
    paddingBlock: 2,
    background: `linear-gradient(90deg, ${token.colorTextTertiary} 0%, ${token.colorTextTertiary} 40%, ${token.colorText} 50%, ${token.colorTextTertiary} 60%, ${token.colorTextTertiary} 100%)`,
    backgroundSize: '200% 100%',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    animation: `${skxShimmerSweep} 1.8s linear infinite, ${skxFadeLift} ${token.motionDurationSlow} ${token.motionEaseOut}`,
    [reduceMotion]: {
      animation: 'none',
      color: token.colorTextSecondary,
      WebkitTextFillColor: token.colorTextSecondary
    }
  },
  thinkingDots: {
    display: 'inline-flex',
    gap: 1,
    letterSpacing: 1,
    paddingLeft: 1,
    color: token.colorTextSecondary,
    WebkitTextFillColor: token.colorTextSecondary,
    '& > span:nth-of-type(1)': {
      animation: `${skxDotOne} 1.6s ease-in-out infinite`
    },
    '& > span:nth-of-type(2)': {
      animation: `${skxDotTwo} 1.6s ease-in-out infinite`
    },
    '& > span:nth-of-type(3)': {
      animation: `${skxDotThree} 1.6s ease-in-out infinite`
    },
    [reduceMotion]: {
      '& > span': { animation: 'none', opacity: 1 }
    }
  },

  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },

  thinking: {
    '& .ant-think-status-down-icon': {
      display: 'none'
    }
  },

  stepGroup: {
    '& .ant-collapse-item': {
      borderBlockEnd: 0
    },

    '& .ant-collapse-header': {
      paddingBlock: token.paddingXXS,
      paddingInline: 0,
      color: token.colorTextSecondary
    },

    '& .ant-collapse-content-box': {
      paddingBlock: token.paddingXXS,
      paddingInline: 0
    },

    '& .ant-thought-chain-node-collapse-icon': {
      display: 'none'
    }
  },

  stepGroupBlink: {
    '& .ant-collapse-title': {
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      color: token.colorTextSecondary,
      backgroundImage: `linear-gradient(90deg, transparent, ${token.colorText}, transparent)`,
      backgroundSize: '50%',
      backgroundRepeat: 'no-repeat',
      animation: `${skxTextBlink} 1s linear infinite`,
      [reduceMotion]: { animation: 'none' }
    }
  },

  speakerLabel: {
    display: 'block',
    marginBottom: 2,
    color: token.colorTextTertiary,
    fontSize: token.fontSizeSM,
    fontWeight: 600
  },

  reasoningBody: {
    marginTop: 4,
    paddingLeft: 10,
    borderLeft: `2px solid ${token.colorBorderSecondary}`,
    color: token.colorTextSecondary,
    fontSize: token.fontSizeSM,
    lineHeight: 1.55,
    wordBreak: 'break-word'
  },

  toolPayload: {
    margin: '4px 0 2px',
    marginLeft: 19,
    padding: '8px 10px',
    background: token.colorFillQuaternary,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    fontFamily: token.fontFamilyCode,
    fontSize: token.fontSize,
    lineHeight: 1.5,
    color: token.colorTextSecondary,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 260,
    overflow: 'auto'
  },

  mermaidSkeleton: {
    minHeight: token.controlHeightLG * 3,

    '& .ant-skeleton': {
      width: '100%'
    },

    '& .ant-skeleton-paragraph': {
      marginBlockEnd: 0,
      paddingInlineStart: 0
    }
  },

  mermaidViewer: {
    position: 'relative',
    width: '100%',
    height: 'min(70vh, 520px)',
    overflow: 'hidden',
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius
  },

  mermaidTransformWrapper: {
    width: '100% !important',
    height: '100% !important'
  },

  mermaidTransformContent: {
    minWidth: '100%',
    minHeight: '100% !important',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  mermaidZoomControls: {
    position: 'absolute',
    bottom: token.paddingSM,
    insetInlineEnd: token.paddingSM,
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 4,
    background: token.colorBgElevated,
    borderRadius: token.borderRadius,
    boxShadow: token.boxShadowSecondary
  },

  approvalCard: {
    marginTop: 4,
    padding: '8px 10px',
    background: token.colorFillQuaternary,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },

  approvalHeader: {
    color: token.colorText
  },

  approvalHeaderText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column'
  },

  approvalKicker: {
    fontSize: token.fontSize - 2,
    lineHeight: 1.3,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: token.colorTextTertiary
  },

  approvalToolName: {
    fontFamily: token.fontFamilyCode,
    fontSize: token.fontSize,
    lineHeight: 1.25,
    color: token.colorText
  },

  confidenceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },

  confidenceBar: {
    flex: 1,
    margin: 0
  },

  confidenceValue: {
    flex: '0 0 auto',
    fontSize: token.fontSizeSM,
    color: token.colorTextSecondary,
    fontVariantNumeric: 'tabular-nums'
  },

  approvalDivider: {
    marginBlockStart: 0,
    marginBlockEnd: 4,
    marginInline: 0,
    borderBlockStartColor: token.colorBorderSecondary
  },

  approvalWhat: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },

  approvalWhatText: {
    fontSize: token.fontSizeSM,
    lineHeight: 1.45,
    color: token.colorTextSecondary
  },

  approvalArgs: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: 10,
    rowGap: 2,
    marginTop: 4
  },

  approvalArgLabel: {
    fontSize: token.fontSizeSM,
    lineHeight: 1.45,
    color: token.colorTextTertiary,
    whiteSpace: 'nowrap'
  },

  approvalArgValue: {
    fontSize: token.fontSizeSM,
    lineHeight: 1.45,
    color: token.colorText,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere'
  },

  approvalActions: {
    marginTop: 6
  },

  questionCard: {
    marginTop: 4,
    padding: '8px 10px',
    background: token.colorInfoBg,
    border: `1px solid ${token.colorInfoBorder}`,
    borderRadius: token.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },

  questionHeader: {
    color: token.colorText
  },

  questionKicker: {
    fontSize: token.fontSize - 2,
    lineHeight: 1.3,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: token.colorInfo
  },

  questionText: {
    fontSize: token.fontSizeSM,
    lineHeight: 1.45,
    color: token.colorText,
    wordBreak: 'break-word'
  },

  questionSummaryLabel: {
    color: token.colorTextSecondary,
    fontSize: token.fontSizeSM,
    fontWeight: token.fontWeightStrong
  },

  questionSelection: {
    color: token.colorText,
    fontSize: token.fontSizeSM,
    lineHeight: 1.4,
    wordBreak: 'break-word'
  },

  questionOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },

  questionOption: {
    appearance: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    textAlign: 'left',
    padding: '6px 10px',
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    color: token.colorText,
    fontFamily: 'inherit',
    fontSize: token.fontSizeSM,
    lineHeight: 1.3,
    cursor: 'pointer',
    transition: `border-color ${token.motionDurationFast} ${token.motionEaseOut}, background ${token.motionDurationFast} ${token.motionEaseOut}`,

    '&:hover': {
      borderColor: token.colorInfoBorderHover,
      background: token.colorInfoBg
    },

    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6
    }
  },

  questionOptionActive: {
    borderColor: token.colorInfo,
    background: token.colorInfoBg,

    '&:hover': {
      borderColor: token.colorInfo
    }
  },

  questionRadio: {
    flex: '0 0 auto',
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: `1.5px solid ${token.colorBorder}`,
    position: 'relative',
    transition: `border-color ${token.motionDurationFast} ${token.motionEaseOut}`
  },

  questionRadioActive: {
    borderColor: token.colorInfo,

    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 3,
      borderRadius: '50%',
      background: token.colorInfo
    }
  },

  questionCheckbox: {
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    borderRadius: token.borderRadiusSM,
    border: `1.5px solid ${token.colorBorder}`,
    color: token.colorTextLightSolid,
    transition: `all ${token.motionDurationFast} ${token.motionEaseOut}`
  },

  questionCheckboxActive: {
    borderColor: token.colorInfo,
    background: token.colorInfo
  },

  questionOptionLabel: {
    flex: 1,
    minWidth: 0
  },

  questionOtherRow: {
    marginTop: 2
  },

  questionActions: {
    marginTop: 4
  },

  markdown: {
    '& > :first-child': { marginTop: 0 },
    '& > :last-child': { marginBottom: 0 },

    '& p': { margin: '6px 0' },

    '& ul, & ol': { margin: '6px 0', paddingInlineStart: 22 },
    '& li': { marginBottom: 2 },
    '& li > p': { margin: 0 },

    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '12px 0 4px',
      lineHeight: 1.3,
      fontWeight: 600
    },
    '& h1': { fontSize: token.fontSizeHeading4 },
    '& h2': { fontSize: token.fontSizeXL },
    '& h3': { fontSize: token.fontSizeLG },
    '& h4, & h5, & h6': { fontSize: token.fontSize },

    '& strong': { fontWeight: 600 },

    '& a': { color: token.colorPrimary },

    '& blockquote': {
      margin: '6px 0',
      paddingInlineStart: 12,
      borderInlineStart: `3px solid ${token.colorBorderSecondary}`,
      color: token.colorTextSecondary
    },

    '& hr': {
      margin: '12px 0',
      border: 'none',
      borderTop: `1px solid ${token.colorBorderSecondary}`
    },

    '& code': {
      fontFamily: token.fontFamilyCode,
      fontSize: 'inherit',
      padding: '1px 5px',
      background: token.colorFillSecondary,
      color: token.colorTextSecondary,
      borderRadius: token.borderRadiusSM
    },

    '& pre': {
      margin: '8px 0',
      padding: '10px 12px',
      background: token.colorFillSecondary,
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: token.borderRadius,
      overflowX: 'auto',
      fontSize: token.fontSizeSM,
      lineHeight: 1.55
    },

    '& pre code': {
      padding: 0,
      background: 'transparent',
      color: 'inherit',
      fontSize: 'inherit',
      borderRadius: 0
    },

    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      margin: '8px 0',
      fontSize: token.fontSizeSM,
      border: `1px solid ${token.colorBorderSecondary}`,
      borderRadius: token.borderRadius,
      overflow: 'hidden'
    },

    '& thead': { background: token.colorFillTertiary },

    '& th, & td': {
      padding: '6px 10px',
      textAlign: 'left',
      borderBottom: `1px solid ${token.colorBorderSecondary}`
    },

    '& th': {
      fontSize: token.fontSize,
      fontWeight: 600,
      color: token.colorTextSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    },

    '& tbody tr:last-child td': { borderBottom: 'none' },

    '& .skx-md-alert': {
      margin: '8px 0',
      padding: '8px 12px',
      borderInlineStart: `3px solid ${token.colorBorder}`,
      borderRadius: token.borderRadiusSM,
      background: token.colorFillQuaternary
    },
    '& .skx-md-alert > :last-child': { marginBottom: 0 },
    '& .skx-md-alert-title': {
      margin: '0 0 4px',
      fontSize: token.fontSizeSM,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    },
    '& .skx-md-alert-note': { borderInlineStartColor: token.colorInfo },
    '& .skx-md-alert-note .skx-md-alert-title': { color: token.colorInfo },
    '& .skx-md-alert-tip': { borderInlineStartColor: token.colorSuccess },
    '& .skx-md-alert-tip .skx-md-alert-title': { color: token.colorSuccess },
    '& .skx-md-alert-important': {
      borderInlineStartColor: token.colorPrimary
    },
    '& .skx-md-alert-important .skx-md-alert-title': {
      color: token.colorPrimary
    },
    '& .skx-md-alert-warning': { borderInlineStartColor: token.colorWarning },
    '& .skx-md-alert-warning .skx-md-alert-title': {
      color: token.colorWarning
    },
    '& .skx-md-alert-caution': { borderInlineStartColor: token.colorError },
    '& .skx-md-alert-caution .skx-md-alert-title': { color: token.colorError }
  },

  historyGroupLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: token.colorTextTertiary,
    padding: '10px 10px 4px'
  },

  historyRow: {
    position: 'relative',
    padding: '6px 10px',
    margin: '0 4px',
    borderRadius: token.borderRadius,
    cursor: 'pointer',
    transition: `background-color ${token.motionDurationMid}`,

    '&:hover': {
      backgroundColor: token.colorFillTertiary
    },

    '&[data-active="true"]': {
      backgroundColor: token.colorPrimaryBg,
      color: token.colorPrimaryText
    },

    '&:hover .skx-history-meta, &:focus-within .skx-history-meta, &[data-overlay-actions-visible="true"] .skx-history-meta, &[data-actions-open="true"] .skx-history-meta':
      {
        opacity: 0
      },

    '&:hover .skx-history-actions, &:focus-within .skx-history-actions, &[data-overlay-actions-visible="true"] .skx-history-actions, &[data-actions-open="true"] .skx-history-actions':
      {
        opacity: 1,
        pointerEvents: 'auto',
        visibility: 'visible'
      },

    '&:focus-visible': {
      outline: `2px solid ${token.colorPrimary}`,
      outlineOffset: 2
    }
  },

  historyRowIcon: {
    color: token.colorTextSecondary,
    flex: '0 0 auto'
  },

  historyRowStatus: {
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 13
  },

  configBanner: {
    width: '100%',
    margin: '0 0 12px'
  },

  conversationTitle: {
    fontSize: token.fontSize + 1
  },

  historyRowMain: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    background: 'transparent',
    padding: 0,
    textAlign: 'left',
    cursor: 'pointer',
    color: 'inherit',

    '&:focus-visible': {
      outline: `2px solid ${token.colorPrimary}`,
      outlineOffset: 2,
      borderRadius: token.borderRadiusSM
    }
  },

  historyRowText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column'
  },

  historyRowTitle: {
    flex: 1,
    minWidth: 0,
    fontSize: token.fontSizeSM,
    fontWeight: 500
  },

  historyRowTime: {
    fontSize: 11,
    whiteSpace: 'nowrap'
  },

  historyRowMeta: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    transition: `opacity ${token.motionDurationFast} ${token.motionEaseOut}`
  },

  historyRowCheck: {
    color: token.colorPrimary,
    flex: '0 0 auto'
  },

  historyRowActions: {
    position: 'absolute',
    insetInlineEnd: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    opacity: 0,
    pointerEvents: 'none',
    visibility: 'hidden',
    transition: `opacity ${token.motionDurationFast} ${token.motionEaseOut}, visibility ${token.motionDurationFast}`
  },

  historyRowActionsStacked: {
    top: 'auto',
    insetBlockEnd: token.paddingXXS,
    transform: 'none'
  },

  historyRowActionButton: {
    color: token.colorTextSecondary,

    '&:hover, &:focus-visible': {
      color: token.colorText
    }
  },

  historyRowInput: {
    flex: 1,
    minWidth: 0
  },

  historySearch: {
    flex: '0 0 auto',
    padding: '8px 10px',
    borderBottom: `1px solid ${token.colorBorderSecondary}`
  },

  historySearchIcon: {
    color: token.colorTextTertiary
  },

  historyList: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '6px 4px 16px'
  },

  historyEmpty: {
    marginTop: 32
  },

  historySkeletonList: {
    paddingBlock: token.paddingXXS
  },

  historySkeletonRow: {
    padding: '6px 10px',
    margin: '0 4px'
  },

  historySkeletonIcon: {
    flex: '0 0 auto',

    '& .ant-skeleton-avatar': {
      width: 13,
      height: 13,
      minWidth: 13,
      borderRadius: token.borderRadiusXS
    }
  },

  historySkeletonTitle: {
    flex: 1,
    minWidth: 0,
    lineHeight: 1,

    '& .ant-skeleton-input': {
      width: '100%',
      minWidth: 0,
      height: token.fontSizeSM
    }
  },

  conversationSkeleton: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: token.marginLG,
    padding: token.paddingMD,
    overflow: 'hidden'
  },

  userSkeletonRow: {
    display: 'flex',
    justifyContent: 'flex-end'
  },

  userSkeletonBubble: {
    width: '45%',

    '& .ant-skeleton-input': {
      width: '100%',
      minWidth: 0,
      height: token.controlHeightLG,
      borderRadius: token.borderRadiusLG
    }
  },

  aiSkeletonBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: token.marginXS
  },

  aiSkeletonName: {
    '& .ant-skeleton-input': {
      width: 96,
      minWidth: 0,
      height: token.fontSize
    }
  },

  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: token.sizeXXS,
    textAlign: 'center',
    width: '100%',
    maxWidth: 640,
    marginInline: 'auto',
    paddingInline: token.paddingLG
  },

  suggestionsPrompts: {
    width: '100%',
    marginTop: token.marginMD
  },

  suggestionsList: {
    justifyContent: 'center',
    gap: token.marginXS
  },

  suggestionItem: {
    '&&&': {
      alignItems: 'center',
      flex: '0 1 auto',
      maxWidth: SUGGESTION_CARD_MAX_WIDTH,
      padding: `${token.paddingXS}px ${token.paddingSM}px`,
      borderRadius: token.borderRadiusLG,

      '& .ant-prompts-item-label': {
        fontSize: token.fontSize,
        fontWeight: 400,
        marginBottom: 0
      },

      '& .ant-prompts-item-content': {
        gap: token.marginXS
      }
    }
  },

  heroTopBar: {
    display: 'flex',
    width: '100%',
    maxWidth: HERO_COLUMN_WIDTH,
    margin: '0 auto',
    padding: `${token.paddingXS}px ${token.paddingLG}px 0`,

    '> *': {
      marginInlineStart: -token.paddingXS
    }
  },

  welcomeStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
    maxWidth: HERO_COLUMN_WIDTH,
    margin: 'auto',
    padding: `${token.paddingXL}px ${token.paddingLG}px`,
    gap: 28
  },

  welcomeAvatar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    width: token.controlHeightLG + token.controlHeightSM,
    height: token.controlHeightLG + token.controlHeightSM,
    borderRadius: token.borderRadiusLG + token.borderRadius,
    background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryHover} 100%)`,
    color: token.colorTextLightSolid,
    boxShadow: `0 6px 18px color-mix(in srgb, ${token.colorPrimary} 25%, transparent)`
  },

  welcomeAvatarPlate: {
    boxShadow: `0 6px 18px color-mix(in srgb, ${token.colorPrimary} 25%, transparent)`
  },

  welcomeCopy: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: token.marginXXS
  },

  welcomeGreeting: {
    fontSize: token.fontSize
  },

  welcomeTitle: {
    '&&': {
      fontSize: 26,
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.2,
      color: token.colorTextHeading,
      margin: 0
    }
  },

  welcomeSubtitle: {
    maxWidth: 520,
    fontSize: token.fontSize
  },

  welcomeComposerWrap: {
    width: '100%'
  },

  welcomeComposer: {
    '&&& .ant-sender-content': {
      paddingBlock: `${token.paddingSM}px !important`
    }
  },

  attachmentTile: {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: token.sizeXS,
    minWidth: 0,
    minHeight: token.controlHeightLG,
    paddingBlock: token.paddingXXS,
    paddingInline: token.paddingSM,
    background: token.colorBgContainer,
    border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    color: token.colorText,
    font: 'inherit',
    textAlign: 'start',
    cursor: 'pointer',
    transition: `background-color ${token.motionDurationFast} ${token.motionEaseOut}`,

    '&:hover': {
      background: token.colorFillTertiary
    },

    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'var(--focus-ring)'
    },

    '&[aria-busy="true"]': {
      cursor: 'progress'
    }
  },

  attachmentIcon: {
    flex: 'none',
    color: token.colorTextSecondary
  },

  attachmentName: {
    maxWidth: ATTACHMENT_NAME_MAX_WIDTH
  },

  attachmentMeta: {
    flex: 'none',
    fontSize: token.fontSizeSM,
    whiteSpace: 'nowrap'
  },

  attachmentThumbnail: {
    appearance: 'none',
    display: 'block',
    padding: 0,
    background: token.colorFillQuaternary,
    border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius,
    overflow: 'hidden',
    cursor: 'pointer',

    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'var(--focus-ring)'
    },

    '& img': {
      display: 'block',
      height: ATTACHMENT_THUMBNAIL_HEIGHT,
      maxWidth: ATTACHMENT_THUMBNAIL_MAX_WIDTH,
      objectFit: 'cover'
    }
  },

  attachmentChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: token.sizeXS,
    minWidth: 0,
    height: token.controlHeight,
    paddingInlineStart: token.paddingXS,
    paddingInlineEnd: token.paddingXXS,
    background: token.colorBgContainer,
    border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
    borderRadius: token.borderRadius
  },

  attachmentChipFailed: {
    borderColor: token.colorErrorBorder,
    background: token.colorErrorBg
  },

  attachmentErrorIcon: {
    flex: 'none',
    color: token.colorError
  },

  attachmentFailure: {
    maxWidth: ATTACHMENT_NAME_MAX_WIDTH,
    fontSize: token.fontSizeSM
  },

  filesList: {
    margin: 0,
    padding: 0,
    listStyle: 'none'
  },

  filesListRow: {
    paddingBlock: token.paddingXS,
    borderBottom: `${token.lineWidth}px ${token.lineType} ${token.colorSplit}`,

    '&:last-child': {
      borderBottom: 0
    }
  },

  filesListBody: {
    flex: 1,
    minWidth: 0
  },

  filesListMeta: {
    fontSize: token.fontSizeSM
  }
}))
